import classNames from 'classnames';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { FC, ReactElement, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import branchesModalStore from '../../store/branchesModalStore';
import { branchStore } from 'src/mobx/branchStore';
import { IBranch } from 'src/types/branches';
import { getTranslate } from 'src/utils/helpers';
import BranchBusy from 'src/view/containers/BranchBusy/BranchBusy';
import { modalStore } from '~/mobx/modalStore';
import { BranchesModalProfile } from '~/view/containers/Modals/BranchesModal/BranchesModal';
import CustomTooltip from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import { TOOLTIP_POSITION } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import { appStore } from '~/mobx/appStore';
import { toastError } from '~/utils/toasts';
import { localeStore } from '~/mobx/localesStore';
import { franchiseManagementStore } from '~/view/screens/SettingsScreen/screens/Orders/screens/FranchiseManagement/store/franchiseManagementStore';
interface IProps {
  branch?: IBranch;
}

export const BranchesModalBranch: FC<IProps> = observer(({ branch }: IProps): ReactElement => {
  const isActive = useMemo((): boolean => {
    return branchesModalStore.selectedBranches.some((_branch) => _branch.id === branch?.id);
  }, [branchesModalStore.selectedBranches, branch]);

  const isUnavailable = useMemo((): boolean => {
    if (!branch) return false;
    if (branchesModalStore.isFreezeBranch(branch)) return true;
    return Boolean(branchesModalStore.isHighlightUnavailable && branchesModalStore.isUnavailable(branch));
  }, [branch, branchesModalStore.isHighlightUnavailable]);

  const { profileNamesToShow } = modalStore.branchesModal || {};

  const clickItem = useCallback(() => {
    const franchiseProfileNotDefaultWithBranches = !franchiseManagementStore.isSelectedDefaultProfile && branchesModalStore.selectedBranches.length;
    const selectedBranchWithPos = branchesModalStore.selectedBranches[0]?.pos_service_id;
    const checkToastShow =
      franchiseProfileNotDefaultWithBranches &&
      ((selectedBranchWithPos === '' && branch?.pos_service_id !== '') || (selectedBranchWithPos !== '' && branch?.pos_service_id === ''));
    if (
      checkToastShow &&
      appStore.settings?.is_pos &&
      profileNamesToShow &&
      profileNamesToShow.includes(BranchesModalProfile.FRANCHISE) &&
      profileNamesToShow.length === 1
    ) {
      toastError({ id: 'error', message: localeStore.t('franchisee_profile_error') });
      return;
    }
    if (isUnavailable || !branch) return;

    branchesModalStore.handlerSelectBranches(branch);
  }, [isUnavailable, branch]);

  const profileNames = useMemo((): string[] => {
    if (!branch || !profileNamesToShow || branchesModalStore.isBusy) {
      return [];
    }

    const profileNamesList = [];

    if (profileNamesToShow.includes(BranchesModalProfile.DELIVERY) && branch.profile_name) {
      profileNamesList.push(branch.profile_name);
    }

    if (appStore.isAddonsAvailable.franchise && profileNamesToShow.includes(BranchesModalProfile.FRANCHISE) && branch.franchise_profile) {
      profileNamesList.push(branch.franchise_profile);
    }

    if (profileNamesToShow.includes(BranchesModalProfile.PREPARATION) && branch.preparation?.name) {
      profileNamesList.push(branch.preparation.name);
    }

    if (profileNamesToShow.includes(BranchesModalProfile.HOURS) && branch.branch_profile_name) {
      profileNamesList.push(branch.branch_profile_name);
    }

    return profileNamesList;
  }, [branchesModalStore.isBusy, modalStore.branchesModal, branch]);

  const className = useMemo((): string => {
    return classNames('custom-modal__item mb-2 mr-2', {
      'custom-modal__item_active': isActive,
      'custom-modal__item_no-available': isUnavailable,
    });
  }, [isActive, isUnavailable]);

  const actualBranch: IBranch | undefined = branchStore.branches.find((_branch: IBranch) => _branch.id === branch?.id) || branch;

  return (
    <>
      {actualBranch ? (
        <li className={className}>
          <CustomTooltip
            show={isUnavailable && Boolean(modalStore.branchesModal?.unavailableTooltipMessage)}
            message={modalStore.branchesModal?.unavailableTooltipMessage}
            wrapperClassName="full-width full-height"
            contentClassName="d-flex"
            position={TOOLTIP_POSITION.PORTAL}
          >
            <div className="custom-modal__item__name-wrapper px-3" onClick={clickItem}>
              <p className="custom-modal__item__name">{getTranslate(actualBranch.name)}</p>
            </div>
            {branchesModalStore.isBusy ? <BranchBusy branch={toJS(actualBranch)} className="mr-3" /> : null}
            {profileNames?.map((profileName) => (
              <div className="custom-modal__item__profile-name-wrapper" onClick={clickItem}>
                <p className="custom-modal__item__profile-name letter-spacing-12 px-2 py-1">{profileName}</p>
              </div>
            ))}
          </CustomTooltip>
        </li>
      ) : (
        <div className="custom-modal__item custom-modal__item__skeleton mb-2 mr-2">
          <Skeleton width="100%" height="100%" />
        </div>
      )}
    </>
  );
});
